import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

type RouterValue = {
  /** Current pathname */
  pathname: string;
  /** Goes to a path inside the app */
  navigate: (to: string) => void;
};

const RouterContext = createContext<RouterValue>({
  pathname: "/",
  navigate: () => undefined,
});

type RouterProviderProps = {
  /** The app */
  children: ReactNode;
};

/**
 * History API routing for a handful of pages. CloudFront serves index.html
 * for every path without a file extension (ci-portal/scripts/setup.sh).
 */
export const RouterProvider = ({ children }: RouterProviderProps) => {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = (to: string) => {
    if (to !== window.location.pathname + window.location.search) {
      window.history.pushState(null, "", to);
    }
    setPathname(new URL(to, window.location.origin).pathname);
    window.scrollTo(0, 0);
  };
  return (
    <RouterContext.Provider value={{ pathname, navigate }}>
      {children}
    </RouterContext.Provider>
  );
};

/** Current path and navigate(). */
export const useRouter = (): RouterValue => useContext(RouterContext);

/** Parameters of `pattern` (e.g. `/mocks/:id`) in `pathname`, or null when it doesn't match. */
export const matchPath = (
  pattern: string,
  pathname: string,
): Record<string, string> | null => {
  const want = pattern.split("/").filter(Boolean);
  const have = pathname.split("/").filter(Boolean);
  if (want.length !== have.length) {
    return null;
  }
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    if (want[i]!.startsWith(":")) {
      params[want[i]!.slice(1)] = decodeURIComponent(have[i]!);
    } else if (want[i] !== have[i]) {
      return null;
    }
  }
  return params;
};

type LinkProps = {
  /** Path inside the app */
  to: string;
  /** Classes of the anchor */
  className?: string;
  /** Link content */
  children: ReactNode;
};

/** An anchor that navigates without a page load (and still opens in a new tab). */
export const Link = ({ to, className, children }: LinkProps) => {
  const { navigate } = useRouter();
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} className={className} onClick={onClick}>
      {children}
    </a>
  );
};
